import {map} from 'lodash';
import {Fragment, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {WritingOptions} from 'xlsx';

import {outgoingMessagesApi} from 'api';
import {Button, GlobalLoader, notifier} from 'components';
import {APP_NAME} from 'constants/app';
import {TaskStatus} from 'enums';
import {TRANSLATION_KEYS} from 'locale';
import {DataRequest} from 'services';
import {OutgoingMessageChannel, OutgoingMessageFeature, OutgoingMessageFilters, OutgoingMessageStatus, PaginatedData} from 'types';
import {buildTranslationKey} from 'utils';
import {lazyModules} from 'utils/lazy-modules';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.OUTGOING_MESSAGES;
const T_KEYS_COMMON = TRANSLATION_KEYS.COMMON;

type Props = {
    className?: string;
    filters: OutgoingMessageFilters;
}

export enum Columns {
    Id = 'ID',
    CreatedAt = 'CREATED_AT',
    CreatedBy = 'CREATED_BY',
    Channel = 'CHANNEL',
    Feature = 'FEATURE',
    Status = 'STATUS',
    Recipient = 'RECIPIENT',
}

export const DownloadMessagesButton: React.FC<Props> = ({className, filters}) => {
    const {t} = useTranslation();

    const [reportLoading, setReportLoading] = useState(false);
    const [reportRequestProgress, setReportRequestProgress] = useState(0);

    const columns = useMemo(() => [
        Columns.Id,
        Columns.CreatedAt,
        Columns.CreatedBy,
        Columns.Channel,
        Columns.Feature,
        Columns.Status,
        Columns.Recipient,
    ], []);

    const requestLabels = useMemo(() => ({
        channel: {
            [OutgoingMessageChannel.Sms]: t(T_KEYS_COMMON.OUTGOING_MESSAGE_CHANNEL[buildTranslationKey(OutgoingMessageChannel.Sms)]),
            [OutgoingMessageChannel.Email]: t(T_KEYS_COMMON.OUTGOING_MESSAGE_CHANNEL[buildTranslationKey(OutgoingMessageChannel.Email)]),
        },
        status: {
            [OutgoingMessageStatus.Sent]: t(T_KEYS_COMMON.OUTGOING_MESSAGE_STATUS[buildTranslationKey(OutgoingMessageStatus.Sent)]),
            [OutgoingMessageStatus.Error]: t(T_KEYS_COMMON.OUTGOING_MESSAGE_STATUS[buildTranslationKey(OutgoingMessageStatus.Error)]),
        },
        feature: {
            [OutgoingMessageFeature.ClientConnectNotification]:
                t(T_KEYS_COMMON.OUTGOING_MESSAGE_FEATURE[buildTranslationKey(OutgoingMessageFeature.ClientConnectNotification)]),
        }
    }), [t]);

    const buildJSON = useCallback((reportData: string[][]) =>
        map(reportData, (message) => columns.reduce((acc, column, index) => {
            acc[t(T_KEYS.COLUMNS[column])] = message[index];
            return acc;
        }, {})),
    [t, columns]);

    const handleComplete = useCallback(async (items: string[][]) => {
        const fileExtension = 'xlsx';
        const fileName = `${t(T_KEYS.FILE_NAME)}.${fileExtension}`;

        const xlsx = await lazyModules.xlsx();
        if (!xlsx) {
            return;
        }

        const wb = xlsx.utils.book_new();
        const sheetName = t(T_KEYS.SHEET_NAME);

        const writeOpts: WritingOptions = {
            bookType: fileExtension,
            bookSST: true,
            type: 'binary',
            sheet: sheetName
        };

        try {
            const json = buildJSON(items);
            const header = map(columns, (col) => t(T_KEYS.COLUMNS[col]));

            const ws = xlsx.utils.json_to_sheet(json, {header});

            wb.SheetNames.push(sheetName);
            wb.Sheets[sheetName] = ws;
            wb.Props = {Author: APP_NAME};

            xlsx.writeFile(wb, fileName, writeOpts);
        } catch {
            notifier.error({title: t(T_KEYS.ERROR.FILE_GENERATE)});
        }
    }, [t, buildJSON, columns]);

    const dataRequest = useMemo(() => new DataRequest<PaginatedData<string[]>>({
        mergeResults: (acc, current) => [...(acc || []), ...current.items],
        formatResponse: (data) => ({
            status: data.page === data.pages ? TaskStatus.Completed : TaskStatus.InProgress,
            progress: Math.round(data.page / data.pages * 100),
            data
        }),
        onRequest: (data) => {
            const _filters = {
                ...filters,
            };

            return outgoingMessagesApi.exportHistory(_filters, {page: data?.page ? (data.page + 1) : 1}, requestLabels);
        },
        onProgress: setReportRequestProgress,
        onSuccess: handleComplete,
        onError: () => notifier.error({title: t(T_KEYS.ERROR.EXPORT)}),
        onComplete: () => {
            setReportLoading(false);
            setReportRequestProgress(0);
        }
    }), [filters, t, handleComplete, requestLabels]);

    const handleExport = useCallback(() => {
        if (reportLoading) {
            return;
        }

        setReportLoading(true);
        setReportRequestProgress(0);

        dataRequest.execute();

    }, [dataRequest, reportLoading]);

    useEffect(() => () => {
        dataRequest.cancel();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Fragment>
            {reportLoading && <GlobalLoader value={reportRequestProgress} />}
            <Button
                disabled={reportLoading}
                className={className}
                width='small'
                icon='arrow-down'
                onClick={handleExport}
            >
                {t(T_KEYS.BUTTONS.EXPORT)}
            </Button>
        </Fragment>
    );
};